import { Editor } from '@tinymce/tinymce-react';
import { uniqid } from '../../tools/utils';
import { useRef } from 'react';
import type { Editor as TinyMCEEditor, Plugin } from 'tinymce';
import { useTrads } from "../../contexts/trads/useTrads";


interface TinyMceEditorProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    control: any;
    name: string;
    onChange: (data: string) => void;
    label?:string;
    maxLength?:number;
}
interface FilePickerResponse{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
    mceAction: string;
    src?:string
    alt?: string,
    width?:number,
    height?:number
}

function TinyMceEditor({control,name,onChange,label,maxLength}: TinyMceEditorProps) {
    const id=uniqid(name);
    const {translate}=useTrads();
    const editorRef = useRef<TinyMCEEditor | null>(null);
    return (
        <>
        {label?
            <label htmlFor={id}>{label}</label>
        :null}
        <Editor
            id={id}
            
            onInit={(_, editor) => {
              editorRef.current = editor
            }}
            onBlur={(_,editor) => {

                onChange(editor.getContent());
            }}
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
            initialValue={control._formValues[name] ?? ''}
            licenseKey = "gpl"
            tinymceScriptSrc= '/tinymce/js/tinymce/tinymce.min.js'
            init={{
                height:       500,
                promotion:    false,
                onboarding:   false,
                branding:     false,
                convert_urls: false,
                language:     'fr_FR',
                external_plugins: {
                    fileupload: '/js/plugins/fileupload/plugin.js'
                },

                plugins: [
                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',  
                    'preview', 'anchor', 'searchreplace', 'visualblocks', 'visualchars', 'code', 
                    'fullscreen', 'insertdatetime', 'media', 'table','code', 'help', 'wordcount'
                ],
                toolbar:            'undo redo | formatselect | bold italic backcolor | link  anchor image | alignleft aligncenter ' +
                'alignright alignjustify | table visualblocks | bullist numlist outdent indent | removeformat | fullscreen code | help fileupload',
                menubar:           'file edit view insert format tools table help',
                extended_valid_elements : 'img[loading|class|src|alt|title|width|height],div[itemprop|itemtype|itemscope|class|id],span[itemprop|itemtype|itemscope|class],li[itemprop|itemtype|itemscope|class],a[href|class|itemprop|itemtype|itemscope|target],svg[*],path[*],lite-youtube[videoid|t|params|start|videostartat],lite-vimeo[videoid|t|params|start]',
                image_title:       true,
                automatic_uploads: true,
                images_upload_url: '/ws/files',
                file_picker_types: 'image file media',
                content_css: '/css/style.min.css',



                style_formats: [
                        {title: 'Custom UL', selector: 'ul', classes: 'custom-list'},
                        { title: 'Arrondis', items: [
                                        {title: 'Rounded', selector: 'a,div,span,img', classes: 'rounded'},
                                        {title: 'Rounded Top', selector: 'a,div,span,img', classes: 'rounded-top'},
                                        {title: 'Rounded Right', selector: 'a,div,span,img', classes: 'rounded-right'},
                                        {title: 'Rounded Bottom', selector: 'a,div,span,img', classes: 'rounded-bottom'},
                                        {title: 'Rounded Left', selector: 'a,div,span,img', classes: 'rounded-left'},
                                        {title: 'Rounded Circle', selector: 'a,div,span,img', classes: 'rounded-circle'},
                                        {title: 'Rounded Pill', selector: 'a,div,span,img', classes: 'rounded-pill'},
                                ]
                        },
                        { title: 'FlexBoxes (div)', items: [
                                {title: 'Flex', selector: 'div,ul', classes: 'd-flex'},
                                
                                {title: 'Flex Row', selector: '.d-flex', classes: 'flex-row'},
                                {title: 'Flex Row reverse', selector: '.d-flex', classes: 'flex-row-reverse'},
                                {title: 'Flex Column', selector: '.d-flex', classes: 'flex-column'},
                                {title: 'Flex Column reverse', selector: '.d-flex', classes: 'flex-column-reverse'},
                        

                                {title: 'Justify Start', selector: '.d-flex', classes: 'justify-content-start'},
                                {title: 'Justify End', selector: '.d-flex', classes: 'justify-content-end'},
                                {title: 'Justify Center', selector: '.d-flex', classes: 'justify-content-center'},
                                {title: 'Justify Between', selector: '.d-flex', classes: 'justify-content-between'},
                                {title: 'Justify Around', selector: '.d-flex', classes: 'justify-content-around'},

                                {title: 'Align Start', selector: '.d-flex', classes: 'align-items-start'},
                                {title: 'Align End', selector: '.d-flex', classes: 'align-items-end'},
                                {title: 'Align Center', selector: '.d-flex', classes: 'align-items-center'},
                                {title: 'Align Baseline', selector: '.d-flex', classes: 'align-items-baseline'},
                                {title: 'Align Stretch', selector: '.d-flex', classes: 'align-items-stretch'},
                        ]
                },
                ],
                style_formats_merge: true,
                file_picker_callback: (cb) => {

                    editorRef.current?.windowManager.openUrl({
                        url:    "/tinymce/images/index.html",//external page
                        title:  'Fichiers distants',
                        width:  800,
                        height: 600,
                        onMessage: function (_, message:FilePickerResponse) {
                            if(message.src){
                                cb(message.src, {alt: message.alt,width:message.width,height:message.height});
                            }
                        }
                    });
                },
                setup: function(editor: TinyMCEEditor){
                    if(maxLength){
                        editor.on('input', function () {
                            const wordCountPlugin:Plugin =  editor.plugins.wordcount;
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                            const currentLength:number = wordCountPlugin.body.getCharacterCount();
                            
                            if (currentLength > maxLength) {
                                // Truncate text or notify user if limit is exceeded
                                editor.notificationManager.open({
                                    text: translate('Character limit reached'),
                                    type: 'warning'
                                });
                                editor.setContent(editor.getContent().substring(0, maxLength));
                                editor.focus();
                                editor.selection.select(editor.getBody(),true);
                                editor.selection.collapse(false);
                            }
                        });
                    }
                }
                
            }}
        >
            
        </Editor>
    </>
    );
}  
export default TinyMceEditor;